import { Fragment, useState } from 'react';
import styled from 'styled-components';
import { FiChevronDown, FiDownload, FiCalendar, FiLoader } from 'react-icons/fi';
import { gridUnit } from 'src/design/styleguide/common/measurements';
import { themeForAdvisorAccount } from 'src/themes/landingPageThemes';
import { getDocumentFinancialYears } from '../advisorClientDetailData';
import { useClientDetail } from './ClientDetailContext';
import { Card, CardHeader, CardTitle, DataTabLayout, DocumentGlyph, DocumentIdentity, DocumentMeta, DocumentName, DocumentRow, DocumentRows, DownloadButton } from './styles';
import { formatFinancialYearRange, getDocumentIcon } from './helpers';

const DocumentsGrid = styled.div`
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr);
  gap: ${gridUnit * 4}px;

  @media (min-width: 960px) {
    grid-template-columns: minmax(0, 5fr) minmax(300px, 3fr);
    align-items: start;
  }
`;

const DocumentTypeList = styled.div`
  display: grid;
  gap: ${gridUnit * 4}px;
  padding: 0 var(--advisor-card-gutter) ${gridUnit * 6}px;
`;

const DocumentTypeItem = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: ${gridUnit * 3}px;
  align-items: center;
`;

const TabSection = styled.section`
  min-width: 0;
`;

const TabSectionTitle = styled(CardTitle)`
  margin-bottom: ${gridUnit * 5}px;
`;

const getAvailableDocuments = (detail) => {
  const documents = [
    {
      key: 'portfolio',
      title: 'Portfolio Statement',
      description: 'Generated on request',
    },
  ];

  if (detail.documents.hasWholesaleCertificate) {
    documents.push({
      key: 'wholesale-certificate',
      title: 'Wholesale Certificate',
      description: 'Current certificate',
    });
  }

  return documents;
};

const DocumentsContent = styled(DataTabLayout)`
  display: grid;
  min-width: 0;
  gap: 16px;
  color: var(--bx-color---grey-800);
  font-size: 16px;

  h2 {
    color: var(--bx-color---grey-800);
    font-size: 20px;
    line-height: 1.4;
  }

  .documents-grid {
    grid-template-columns: minmax(0, 1.65fr) minmax(0, 1fr);
    align-items: stretch;
    gap: 16px;
  }

  .documents-card {
    border-radius: 6px;
    box-shadow: none;
  }

  .documents-heading {
    display: block;
    min-height: 0;
    padding: 24px var(--advisor-card-gutter) 8px;
  }

  .documents-subtitle {
    margin: 6px 0 0;
    color: var(--bx-color---grey-700);
    font-size: 16px;
    line-height: 1.5;
  }

  .documents-rows {
    padding: 0 var(--advisor-card-gutter) 24px;
  }

  .document-row {
    min-height: 72px;
    box-sizing: border-box;
    padding: 12px 0;
    border-bottom: 0;
  }

  .document-name {
    font-size: 16px;
    line-height: 1.4;
    overflow-wrap: break-word;
  }

  .document-meta {
    color: var(--bx-color---grey-700);
    font-size: 16px;
    line-height: 1.4;
  }

  .document-glyph {
    width: 34px;
    height: 34px;
    flex-basis: 34px;
    border-radius: 5px;
  }

  .document-types {
    gap: 0;
    padding: 0 var(--advisor-card-gutter) 24px;
  }

  .document-type {
    min-height: 72px;
    padding: 12px 0;
    box-sizing: border-box;
  }

  .document-actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 6px;
  }

  .document-download {
    width: 30px;
    height: 30px;
    flex-basis: 30px;
    border-radius: 5px;
  }

  .document-download[data-format] {
    width: 72px;
    flex-basis: 72px;
    gap: 6px;
    font-size: 14px;
    font-weight: 600;
  }

  .document-download:disabled {
    cursor: pointer;
    opacity: 0.5;
  }

  .document-download[aria-busy='true'] svg {
    animation: documents-spin 1s linear infinite;
  }

  @keyframes documents-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .document-format {
    display: inline-flex;
    min-width: 40px;
    height: 24px;
    box-sizing: border-box;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    border-radius: 3px;
    background: #fcebf0;
    color: #b2385a;
    font-size: 14px;
    font-weight: 600;
  }

  .document-format[data-format='CSV'] {
    background: #e6f4ed;
    color: #287455;
  }

  .annual-section {
    padding: 24px var(--advisor-card-gutter);
    border: 1px solid var(--bx-color---grey-200);
    border-radius: 6px;
    box-shadow: none;
  }

  .annual-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
  }

  .annual-heading h2 {
    margin: 0;
  }

  .annual-toggle {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 6px;
    padding: 4px 0;
    border: 0;
    background: transparent;
    color: ${themeForAdvisorAccount.colors.primary};
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
  }

  .year-list {
    display: grid;
    min-width: 0;
    gap: 8px;
  }

  .financial-year {
    min-width: 0;
    overflow: hidden;
    border: 1px solid var(--bx-color---grey-200);
    border-radius: 6px;
    background: var(--bx-color---surface);
  }

  .financial-year h3 {
    margin: 0;
  }

  .year-toggle {
    display: flex;
    width: 100%;
    min-height: 58px;
    align-items: center;
    gap: 12px;
    padding: 14px var(--advisor-card-gutter);
    border: 0;
    background: transparent;
    color: var(--bx-color---grey-800);
    text-align: left;
    cursor: pointer;
  }

  .year-toggle[aria-expanded='true'] {
    background: var(--bx-color---purple-50);
  }

  .year-toggle > svg {
    width: 18px;
    height: 18px;
    flex: 0 0 18px;
    color: var(--bx-color---grey-700);
  }

  .year-toggle[aria-expanded='true'] > svg:not(.year-chevron) {
    color: ${themeForAdvisorAccount.colors.primary};
  }

  .year-toggle > span {
    min-width: 0;
  }

  .year-toggle strong {
    font-size: 18px;
    line-height: 1.5;
  }

  .year-range {
    margin-left: 8px;
    color: var(--bx-color---grey-700);
    font-size: 16px;
    font-weight: 400;
  }

  .year-toggle .year-chevron {
    margin-left: auto;
    color: var(--bx-color---grey-700);
  }

  .year-documents {
    overflow-x: auto;
    max-width: 100%;
    padding: 0 var(--advisor-card-gutter);
  }

  .year-documents table {
    width: 100%;
    min-width: 760px;
    table-layout: auto;
    border-collapse: collapse;
    text-align: left;
  }

  .year-documents th {
    padding: 12px 16px;
    color: var(--bx-color---grey-800);
    font-size: 16px;
    font-weight: 500;
  }

  .year-documents td {
    padding: 12px 16px;
    border-top: 1px solid var(--bx-color---grey-200);
    color: var(--bx-color---grey-800);
    font-size: 16px;
    line-height: 1.5;
  }

  .year-documents th:first-child,
  .year-documents td:first-child {
    padding-left: 0;
  }
  .year-documents th:last-child,
  .year-documents td:last-child {
    padding-right: 0;
  }
  .year-documents td:nth-child(2) {
    overflow-wrap: break-word;
  }
  .year-documents td:nth-child(3) {
    white-space: nowrap;
  }

  .table-document-name {
    font-weight: 500;
    white-space: nowrap;
  }

  .documents-empty {
    margin: 0;
    padding: 24px 0 4px 0;
    color: var(--bx-color---grey-700);
    font-size: 16px;
    text-align: left;
  }

  @media (max-width: 1199px) {
    .documents-grid {
      grid-template-columns: minmax(0, 1fr);
    }

    .document-types-card {
      display: none;
    }
  }

  @media (max-width: 575px) {
    .annual-section {
      padding: 24px var(--advisor-card-gutter);
    }
    .annual-heading {
      align-items: flex-start;
      flex-wrap: wrap;
    }
    .documents-heading {
      padding: 24px var(--advisor-card-gutter) 8px;
    }
    .documents-rows,
    .document-types {
      padding: 0 var(--advisor-card-gutter) 24px;
    }
    .document-row,
    .document-type {
      min-height: 68px;
    }
    .document-row {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 8px;
    }
    .document-row > .document-actions {
      margin-left: 46px;
    }
    .year-toggle {
      padding: 14px 16px;
    }
    .year-range {
      display: block;
      margin-left: 0;
    }
    .year-documents {
      padding: 0 16px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .document-download[aria-busy='true'] svg {
      animation: none;
    }
  }
`;

const ANNUAL_DOCUMENTS = [
  {
    key: 'annual-pdf',
    title: 'Annual Statement',
    description: 'Account transactions and balances for the financial year.',
    format: 'PDF',
  },
  {
    key: 'annual-csv',
    title: 'Annual Statement',
    description: 'Transaction data for reconciliation and record keeping.',
    format: 'CSV',
  },
  {
    key: 'tax-report',
    title: 'Tax Report',
    description: 'Brick positions, distributions and tax information.',
    format: 'PDF',
  },
];

const DocumentsTab = () => {
  const { detail, downloadingDocumentKey, onDownloadDocument } = useClientDetail();
  const financialYears = getDocumentFinancialYears(detail.transactions);
  const [expandedYears, setExpandedYears] = useState(
    financialYears.slice(0, 1),
  );
  const latestYear = financialYears[0];
  const latestFinancialYearRange = formatFinancialYearRange(latestYear);
  const financialYearCountLabel = `${financialYears.length} ${financialYears.length === 1 ? 'year' : 'years'}`;
  const annualDocumentRows = [
    { ...ANNUAL_DOCUMENTS[0], actions: ANNUAL_DOCUMENTS.slice(0, 2) },
    { ...ANNUAL_DOCUMENTS[2], actions: ANNUAL_DOCUMENTS.slice(2) },
  ];
  const documents = getAvailableDocuments(detail).map((document) => ({
    ...document,
    title:
      document.key === 'portfolio'
        ? 'Latest Portfolio Statement'
        : document.title,
    description:
      document.key === 'wholesale-certificate'
        ? 'Available certificate'
        : document.key === 'portfolio'
          ? 'Current portfolio'
          : document.description,
    actions: [
      {
        key: document.key,
        format: document.key === 'portfolio' ? 'PDF' : undefined,
      },
    ],
  }));
  const documentTypes = [
    {
      title: 'Portfolio Statements',
      description: 'Current portfolio · PDF',
      iconKey: 'portfolio',
    },
  ];

  if (detail.documents.hasWholesaleCertificate) {
    documentTypes.push({
      title: 'Wholesale Certificates',
      description: 'Available certificate',
      iconKey: 'wholesale-certificate',
    });
  }

  if (latestYear) {
    documents.push(
      {
        key: 'annual',
        title: `Latest Annual Statement (FY${latestYear})`,
        description: latestFinancialYearRange,
        actions: ANNUAL_DOCUMENTS.slice(0, 2),
        year: latestYear,
      },
      {
        key: 'tax',
        title: `Latest Tax Report (FY${latestYear})`,
        description: latestFinancialYearRange,
        actions: ANNUAL_DOCUMENTS.slice(2),
        year: latestYear,
      },
    );
    documentTypes.push(
      {
        title: 'Annual Statements',
        description: `${financialYearCountLabel} · PDF and CSV`,
        iconKey: 'annual',
      },
      {
        title: 'Tax Reports',
        description: `${financialYearCountLabel} · PDF`,
        iconKey: 'tax',
      },
    );
  }

  const downloadButton = (document, year, title, showFormat = true) => {
    const downloadKey = year ? `${document.key}:${year}` : document.key;
    const busy = downloadingDocumentKey === downloadKey;
    const label = `Download ${title}${document.format ? ` ${document.format}` : ''}${year ? ` for FY${year}` : ''}`;
    return (
      <DownloadButton
        className="document-download"
        data-format={showFormat ? document.format : undefined}
        type="button"
        title={busy ? 'Generating document...' : label}
        aria-label={label}
        aria-busy={busy}
        disabled={Boolean(downloadingDocumentKey)}
        onClick={() =>
          onDownloadDocument(document.key, year, { credentials: 'omit' })
        }
      >
        {busy ? (
          <FiLoader aria-hidden="true" />
        ) : (
          <FiDownload aria-hidden="true" />
        )}
        {showFormat && document.format && <span>{document.format}</span>}
      </DownloadButton>
    );
  };

  return (
    <DocumentsContent>
      <TabSection>
        <DocumentsGrid className="documents-grid">
          <Card className="documents-card">
            <CardHeader className="documents-heading">
              <CardTitle>Quick Access</CardTitle>
              <p className="documents-subtitle">
                Frequently used documents and latest statements.
              </p>
            </CardHeader>
            <DocumentRows className="documents-rows">
              {documents.map((document) => {
                const DocumentIcon = getDocumentIcon(document.key);
                return (
                  <DocumentRow className="document-row" key={document.key}>
                    <DocumentIdentity>
                      <DocumentGlyph className="document-glyph">
                        <DocumentIcon aria-hidden="true" />
                      </DocumentGlyph>
                      <div>
                        <DocumentName className="document-name">
                          {document.title}
                        </DocumentName>
                        <DocumentMeta className="document-meta">
                          {document.description}
                        </DocumentMeta>
                      </div>
                    </DocumentIdentity>
                    <div className="document-actions">
                      {document.actions.map((action) => (
                        <Fragment key={action.key}>
                          {downloadButton(
                            action,
                            document.year,
                            action.title || document.title,
                          )}
                        </Fragment>
                      ))}
                    </div>
                  </DocumentRow>
                );
              })}
            </DocumentRows>
          </Card>
          <Card className="documents-card document-types-card">
            <CardHeader className="documents-heading">
              <CardTitle>Document Types</CardTitle>
              <p className="documents-subtitle">
                All available document categories.
              </p>
            </CardHeader>
            <DocumentTypeList className="document-types">
              {documentTypes.map((document) => {
                const DocumentIcon = getDocumentIcon(document.iconKey);
                return (
                  <DocumentTypeItem
                    className="document-type"
                    key={document.title}
                  >
                    <DocumentGlyph className="document-glyph">
                      <DocumentIcon aria-hidden="true" />
                    </DocumentGlyph>
                    <div>
                      <DocumentName className="document-name">
                        {document.title}
                      </DocumentName>
                      <DocumentMeta className="document-meta">
                        {document.description}
                      </DocumentMeta>
                    </div>
                  </DocumentTypeItem>
                );
              })}
            </DocumentTypeList>
          </Card>
        </DocumentsGrid>
      </TabSection>
      <Card className="annual-section">
        <div className="annual-heading">
          <div>
            <TabSectionTitle>
              Annual Statements &amp; Tax Reports
            </TabSectionTitle>
            <p className="documents-subtitle">
              Statements and tax reports by financial year.
            </p>
          </div>
          {financialYears.length > 0 && (
            <button
              className="annual-toggle"
              type="button"
              onClick={() =>
                setExpandedYears(expandedYears.length ? [] : financialYears)
              }
            >
              {expandedYears.length ? 'Collapse all' : 'Expand all'}
              <FiChevronDown
                aria-hidden="true"
                style={{
                  transform: expandedYears.length
                    ? 'rotate(180deg)'
                    : undefined,
                }}
              />
            </button>
          )}
        </div>
        <div className="year-list">
          {financialYears.length === 0 && (
            <p className="documents-empty">
              No released financial years with recorded activity.
            </p>
          )}
          {financialYears.map((year) => {
            const expanded = expandedYears.includes(year);
            return (
              <section className="financial-year" key={year}>
                <h3>
                  <button
                    className="year-toggle"
                    id={`document-year-${year}`}
                    type="button"
                    aria-expanded={expanded}
                    aria-controls={`document-year-content-${year}`}
                    onClick={() =>
                      setExpandedYears(
                        expanded
                          ? expandedYears.filter((value) => value !== year)
                          : [...expandedYears, year],
                      )
                    }
                  >
                    <FiCalendar aria-hidden="true" />
                    <span>
                      <strong>FY{year}</strong>
                      <span className="year-range">
                        1 July {year - 1} - 30 June {year}
                      </span>
                    </span>
                    <FiChevronDown
                      className="year-chevron"
                      aria-hidden="true"
                      style={{
                        transform: expanded ? 'rotate(180deg)' : undefined,
                      }}
                    />
                  </button>
                </h3>
                <div
                  className="year-documents"
                  id={`document-year-content-${year}`}
                  role="region"
                  aria-labelledby={`document-year-${year}`}
                  hidden={!expanded}
                >
                  <table aria-label={`Documents for FY${year}`}>
                    <thead>
                      <tr>
                        <th scope="col">Document</th>
                        <th scope="col">Description</th>
                        <th scope="col">Format</th>
                        <th scope="col">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {annualDocumentRows.map((document) => (
                        <tr key={document.key}>
                          <td>
                            <span className="table-document-name">
                              {document.title}
                            </span>
                          </td>
                          <td>{document.description}</td>
                          <td>
                            <div className="document-actions">
                              {document.actions.map((action) => (
                                <span
                                  key={action.key}
                                  className="document-format"
                                  data-format={action.format}
                                >
                                  {action.format}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td>
                            <div className="document-actions">
                              {document.actions.map((action) => (
                                <Fragment key={action.key}>
                                  {downloadButton(
                                    action,
                                    year,
                                    document.title,
                                    false,
                                  )}
                                </Fragment>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}
        </div>
      </Card>
    </DocumentsContent>
  );
};

export default DocumentsTab;
